import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaComment,
  FaHeart,
  FaImage,
  FaPaperPlane,
  FaRegCommentDots,
  FaRegHeart,
  FaReply,
  FaSpinner,
  FaTrash,
  FaTrophy,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useAppTheme } from "../context/AppThemeContext";
import { MANAGEMENT_ROLES, ROLE_SUPER_ADMIN, ROLE_TEACHER } from "../utils/authRole";

const CATEGORIES = ["Achievement", "Event", "Notice", "Milestone"];
const commentsCache = new Map();

const formatRelativeTime = (value) => {
  if (!value) return "now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "now";

  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return "now";
  if (diff < hour) return `${Math.floor(diff / minute)}m`;
  if (diff < day) return `${Math.floor(diff / hour)}h`;
  return `${Math.floor(diff / day)}d`;
};

const initials = (name = "User") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("") || "U";

const categoryIcon = (cat) => {
  if (cat === "Achievement" || cat === "Milestone") return <FaTrophy className="text-amber-400" />;
  return <FaImage className="text-sky-400" />;
};

const roleBadge = (role) => {
  if (!role) return null;
  const label = role.replace("ROLE_", "").charAt(0) + role.replace("ROLE_", "").slice(1).toLowerCase();
  const tone =
    role === "ROLE_ADMIN" || role === "ROLE_PRINCIPAL"
      ? "bg-violet-100 text-violet-700"
      : role === "ROLE_TEACHER"
        ? "bg-sky-100 text-sky-700"
        : "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>
      {label}
    </span>
  );
};

const normalizePost = (p = {}, idx = 0) => ({
  id: p.id || p.momentId || p._id || `MOM-${1000 + idx}`,
  title: p.title || "Untitled",
  description: p.description || "",
  category: p.category || "Event",
  imageUrl: p.imageUrl || p.url || "",
  authorName: p.authorName || p.author || "Staff",
  authorRole: p.authorRole || "",
  createdAt: p.createdAt || p.date || "",
  likeCount: Number(p.likeCount ?? p.likes ?? 0),
  commentCount: Number(
    p.commentCount ??
      p.commentsCount ??
      p.totalComments ??
      p.comments_count ??
      (Array.isArray(p.comments) ? p.comments.length : p.comments) ??
      0
  ),
  likedByMe: Boolean(p.likedByMe),
  tenantId: String(p.tenantId || p.tenant?.id || p.schoolId || "unassigned"),
  tenantName: p.tenantName || p.tenant?.name || p.schoolName || p.organizationName || "Unassigned tenant",
  visibility: String(p.visibility || p.visibilityStatus || "VISIBLE").toUpperCase(),
});

const normalizeComment = (c = {}, idx = 0) => ({
  id: c.id || c.commentId || c._id || `CMT-${idx + 1}`,
  text: c.text || c.content || "",
  authorName: c.authorName || c.author || "User",
  createdAt: c.createdAt || c.date || "",
  likeCount: Number(c.likeCount ?? c.likes ?? c.commentLikeCount ?? 0),
  likedByMe: Boolean(c.likedByMe),
  replies: Array.isArray(c.replies) ? c.replies.map((r, rIdx) => normalizeComment(r, rIdx)) : [],
});

const extractList = (data) => {
  const payload = data?.data ?? data ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.moments)) return payload.moments;
  return [];
};

const extractCommentList = (data) => {
  const payload = data?.data ?? data ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.comments)) return payload.comments;
  return [];
};

const updateCommentInTree = (list, targetId, updater) =>
  list.map((item) => {
    if (item.id === targetId) return updater(item);
    if (item.replies?.length) {
      return { ...item, replies: updateCommentInTree(item.replies, targetId, updater) };
    }
    return item;
  });

const appendReplyInTree = (list, parentId, reply) =>
  list.map((item) => {
    if (item.id === parentId) {
      return { ...item, replies: [...(item.replies || []), reply] };
    }
    if (item.replies?.length) {
      return { ...item, replies: appendReplyInTree(item.replies, parentId, reply) };
    }
    return item;
  });

// --- Comment thread sub-component ---------------------------------------------
function CommentThread({ postId, isDarkMode, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    if (commentsCache.has(postId)) {
      const cached = commentsCache.get(postId) || [];
      setComments(cached);
      onCountChange?.(cached.length);
      setLoading(false);
      return;
    }

    api
      .get(API_PATHS.moments.comments(postId))
      .then((res) => {
        const list = extractCommentList(res.data).map(normalizeComment);
        commentsCache.set(postId, list);
        setComments(list);
        onCountChange?.(list.length);
      })
      .catch(() => {
        setComments([]);
      })
      .finally(() => setLoading(false));
  }, [postId, onCountChange]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.post(API_PATHS.moments.comments(postId), { text: text.trim() });
      const newComment = normalizeComment(res.data?.data ?? res.data ?? { text: text.trim() });
      setComments((c) => {
        const next = [...c, newComment];
        commentsCache.set(postId, next);
        onCountChange?.(next.length);
        return next;
      });
      setText("");
    } catch {
      toast.error("Could not post comment.");
    } finally {
      setSending(false);
    }
  };

  const toggleCommentLike = async (commentId) => {
    const previous = comments;
    const target = previous.find((c) => c.id === commentId) || null;
    const currentlyLiked = target?.likedByMe;

    const optimistic = updateCommentInTree(previous, commentId, (item) => ({
      ...item,
      likedByMe: !item.likedByMe,
      likeCount: Math.max(0, item.likeCount + (item.likedByMe ? -1 : 1)),
    }));

    setComments(optimistic);
    commentsCache.set(postId, optimistic);

    const endpoint = currentlyLiked
      ? API_PATHS.moments.commentUnlike(postId, commentId)
      : API_PATHS.moments.commentLike(postId, commentId);

    try {
      await api.post(endpoint);
    } catch {
      setComments(previous);
      commentsCache.set(postId, previous);
      toast.error("Could not update comment like.");
    }
  };

  const submitReply = async (parentCommentId) => {
    if (!replyText.trim() || sendingReply) return;

    setSendingReply(true);
    try {
      const endpoint = API_PATHS.moments.commentReplies(postId, parentCommentId);
      let replyPayload = null;

      try {
        const res = await api.post(endpoint, { text: replyText.trim() });
        replyPayload = res.data?.data ?? res.data;
      } catch (error) {
        toast.error(error?.message || "Could not post reply.");
        return;
      }

      const reply = normalizeComment(replyPayload);
      setComments((cur) => {
        const next = appendReplyInTree(cur, parentCommentId, reply);
        commentsCache.set(postId, next);
        return next;
      });
      setReplyText("");
      setReplyingTo("");
    } finally {
      setSendingReply(false);
    }
  };

  const CommentItem = ({ comment, isReply = false }) => (
    <li className={`${isReply ? "ml-8 border-l pl-3" : ""} py-2 ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
      <div className="flex items-start gap-2.5">
        <div className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${isDarkMode ? "bg-slate-700 text-slate-200" : "bg-slate-200 text-slate-700"}`}>
          {initials(comment.authorName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{comment.authorName}</span>
            <span className={`text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{formatRelativeTime(comment.createdAt)}</span>
          </div>
          <p className={`mt-0.5 text-sm leading-relaxed ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{comment.text}</p>

          <div className="mt-1 flex items-center gap-4 text-xs">
            <button
              type="button"
              onClick={() => toggleCommentLike(comment.id)}
              className={`inline-flex items-center gap-1 transition-colors ${comment.likedByMe ? "text-rose-500" : isDarkMode ? "text-slate-400 hover:text-rose-400" : "text-slate-500 hover:text-rose-500"}`}
            >
              {comment.likedByMe ? <FaHeart className="text-[11px]" /> : <FaRegHeart className="text-[11px]" />}
              {comment.likeCount}
            </button>
            {!isReply && (
              <button
                type="button"
                onClick={() => {
                  setReplyingTo((cur) => (cur === comment.id ? "" : comment.id));
                  setReplyText("");
                }}
                className={`transition-colors ${isDarkMode ? "text-slate-400 hover:text-sky-400" : "text-slate-500 hover:text-sky-600"}`}
              >
                Reply
              </button>
            )}
          </div>

          {!isReply && replyingTo === comment.id && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitReply(comment.id)}
                placeholder="Write a reply..."
                className={`flex-1 border px-2 py-1.5 text-xs focus:border-sky-500 focus:outline-none ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-300 bg-white"}`}
              />
              <button
                type="button"
                onClick={() => submitReply(comment.id)}
                disabled={!replyText.trim() || sendingReply}
                className="bg-sky-500 px-2 py-1 text-xs font-semibold text-white hover:bg-sky-600 disabled:opacity-50"
              >
                {sendingReply ? <FaSpinner className="animate-spin" /> : <FaReply />}
              </button>
            </div>
          )}

          {comment.replies?.length > 0 && (
            <ul className="mt-3 space-y-3">
              {comment.replies.map((reply, idx) => (
                <CommentItem key={reply.id || idx} comment={reply} isReply />
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );

  return (
    <div className={`mt-2 border-t px-0 pt-2 text-sm ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
      {loading ? (
        <FaSpinner className="animate-spin text-slate-400" />
      ) : comments.length === 0 ? (
        <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No comments yet.</p>
      ) : (
        <ul className="space-y-1">
          {comments.map((c, i) => (
            <CommentItem key={c.id || i} comment={c} />
          ))}
        </ul>
      )}
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Write a comment..."
          className={`flex-1 border px-2 py-1.5 text-xs focus:border-amber-500 focus:outline-none ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-200 placeholder:text-slate-500" : "border-slate-300 bg-white"}`}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || sending}
          className="bg-amber-500 px-2 py-1 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
        >
          {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
        </button>
      </div>
    </div>
  );
}

// --- Post card ----------------------------------------------------------------
function PostCard({ post: initialPost, isDarkMode, canDelete, onDelete, isModerator = false, onVisibilityChange }) {
  const [post, setPost] = useState(initialPost);
  const [showComments, setShowComments] = useState(false);
  const [liking, setLiking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  useEffect(() => {
    setPost(initialPost);
  }, [initialPost]);

  const handleCountChange = useCallback((count) => {
    setPost((p) => (p.commentCount === count ? p : { ...p, commentCount: count }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    // If list API doesn't provide comment count, derive it from comments endpoint.
    if (initialPost.commentCount > 0) {
      return () => {
        isMounted = false;
      };
    }

    api
      .get(API_PATHS.moments.comments(post.id))
      .then((res) => {
        const count = extractCommentList(res.data).length;
        if (isMounted && count !== post.commentCount) {
          setPost((p) => ({ ...p, commentCount: count }));
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [initialPost.commentCount, post.id, post.commentCount]);

  const toggleLike = async () => {
    if (liking) return;
    setLiking(true);
    try {
      if (post.likedByMe) {
        await api.post(API_PATHS.moments.unlike(post.id));
        setPost((p) => ({ ...p, likedByMe: false, likeCount: Math.max(0, p.likeCount - 1) }));
      } else {
        await api.post(API_PATHS.moments.like(post.id));
        setPost((p) => ({ ...p, likedByMe: true, likeCount: p.likeCount + 1 }));
      }
    } catch {
      toast.error("Could not update like.");
    } finally {
      setLiking(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || deleting) return;
    const confirmed = window.confirm("Delete this moment?");
    if (!confirmed) return;

    setDeleting(true);
    try {
      await api.delete(isModerator ? API_PATHS.superAdmin.momentById(post.id) : API_PATHS.moments.deletePost(post.id));
      onDelete?.(post.id);
      toast.success("Moment deleted.");
    } catch {
      toast.error("Could not delete moment.");
    } finally {
      setDeleting(false);
    }
  };

  const handleVisibilityChange = async () => {
    if (!isModerator || updatingVisibility) return;
    const visibility = post.visibility === "VISIBLE" ? "HIDDEN" : "VISIBLE";
    setUpdatingVisibility(true);
    try {
      await api.patch(API_PATHS.superAdmin.momentVisibility(post.id), { visibility });
      setPost((current) => ({ ...current, visibility }));
      onVisibilityChange?.(post.id, visibility);
      toast.success(`Moment is now ${visibility.toLowerCase()}.`);
    } catch (error) {
      toast.error(error?.message || "Could not update moment visibility.");
    } finally {
      setUpdatingVisibility(false);
    }
  };

  return (
    <article className={`overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
      <div className="px-5 pb-4 pt-5">
      <div className="flex gap-3">
        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-black text-white shadow-sm">
          {initials(post.authorName)}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`truncate text-sm font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{post.authorName}</span>
                {roleBadge(post.authorRole)}
                <span className={`text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{formatRelativeTime(post.createdAt)}</span>
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                {categoryIcon(post.category)}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                  {post.category}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
            {isModerator && (
              <>
                <span className={`border px-2 py-1 text-[10px] font-bold tracking-wide ${post.visibility === "VISIBLE" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : post.visibility === "AUTO_HIDDEN" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-300 bg-slate-100 text-slate-600"}`}>{post.visibility.replace("_", " ")}</span>
                <button type="button" onClick={handleVisibilityChange} disabled={updatingVisibility} className={`border px-2 py-1 text-[11px] font-semibold transition-colors ${isDarkMode ? "border-slate-700 text-sky-300 hover:border-sky-500" : "border-slate-200 text-sky-700 hover:border-sky-400"} disabled:opacity-60`}>
                  {updatingVisibility ? <FaSpinner className="animate-spin" /> : post.visibility === "VISIBLE" ? "Hide" : "Make visible"}
                </button>
              </>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                title="Delete moment"
                className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-semibold transition-colors ${isDarkMode ? "border-slate-700 text-slate-400 hover:border-rose-500 hover:text-rose-400" : "border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600"} disabled:opacity-60`}
              >
                {deleting ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                Delete
              </button>
            )}
            </div>
          </div>

          <h3 className={`font-bold text-base leading-snug ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{post.title}</h3>
          {post.description && (
            <p className={`text-sm leading-relaxed ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>{post.description}</p>
          )}

          {post.imageUrl && (
            <img src={post.imageUrl} alt={post.title} className="aspect-video w-full rounded-xl object-cover" />
          )}

          <div className={`mt-1 grid grid-cols-2 border-y py-1 text-sm ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
            <button
              type="button"
              onClick={() => setShowComments((s) => !s)}
              className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1 transition-colors ${isDarkMode ? "text-slate-400 hover:bg-sky-500/10 hover:text-sky-400" : "text-slate-500 hover:bg-sky-50 hover:text-sky-600"}`}
            >
              <FaRegCommentDots />
              <span>{post.commentCount}</span>
            </button>

            <button
              type="button"
              onClick={toggleLike}
              disabled={liking}
              className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1 transition-colors ${
                post.likedByMe
                  ? "text-rose-500"
                  : isDarkMode
                    ? "text-slate-400 hover:bg-rose-500/10 hover:text-rose-400"
                    : "text-slate-500 hover:bg-rose-50 hover:text-rose-500"
              }`}
            >
              {liking ? <FaSpinner className="animate-spin text-xs" /> : post.likedByMe ? <FaHeart /> : <FaRegHeart />}
              <span>{post.likeCount}</span>
            </button>
          </div>

          {showComments && (
            <CommentThread
              postId={post.id}
              isDarkMode={isDarkMode}
              onCountChange={handleCountChange}
            />
          )}
        </div>
      </div>
      </div>
    </article>
  );
}

// --- Main component -----------------------------------------------------------
export default function MomentsPage({ userRole }) {
  const { isDarkMode } = useAppTheme();
  const isModerator = userRole === ROLE_SUPER_ADMIN;
  const canPost = !isModerator && (MANAGEMENT_ROLES.includes(userRole) || userRole === ROLE_TEACHER);
  const canDeletePost = canPost;

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Create form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Achievement");
  const [submitting, setSubmitting] = useState(false);

  const sortedPosts = useMemo(() => [...posts].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)), [posts]);
  const tenantGroups = useMemo(() => {
    if (userRole !== ROLE_SUPER_ADMIN) return [];
    const grouped = new Map();
    sortedPosts.forEach((post) => {
      const key = post.tenantId || post.tenantName;
      if (!grouped.has(key)) grouped.set(key, { name: post.tenantName, posts: [] });
      grouped.get(key).posts.push(post);
    });
    return [...grouped.entries()].map(([id, group]) => ({ id, ...group }));
  }, [sortedPosts, userRole]);

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const res = await api.get(isModerator ? API_PATHS.superAdmin.moments() : API_PATHS.moments.list());
      setPosts(extractList(res.data).map(normalizePost));
    } catch {
      setPosts([]);
      toast.error("Unable to load moments.");
    } finally {
      setLoadingPosts(false);
    }
  }, [isModerator]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error("Title is required."); return; }
    setSubmitting(true);
    try {
      /**
       * JSON payload expected by backend:
       * POST /api/moments/create
       * {
       *   "title"       : "Science Fair Win"  [required]
       *   "description" : "..."               [optional]
       *   "category"    : "Achievement"       [optional, backend may ignore]
       * }
       *
       * Expected response:
       * { "data": { "id": "...", "title": "...", "authorName": "...", "authorRole": "...",
       *             "createdAt": "...", "likeCount": 0, "commentCount": 0, "imageUrl": "..." } }
       */
      const payload = {
        title: title.trim(),
        description: description.trim(),
        category,
      };

      const res = await api.post(API_PATHS.moments.create(), payload);
      const saved = normalizePost(res.data?.data ?? res.data ?? {}, posts.length);
      setPosts((cur) => [saved, ...cur]);
      toast.success("Moment posted!");
      setTitle("");
      setDescription("");
      setCategory("Achievement");
    } catch {
      toast.error("Failed to post moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = useCallback((postId) => {
    setPosts((cur) => cur.filter((item) => item.id !== postId));
    commentsCache.delete(postId);
  }, []);

  const handleVisibilityChange = useCallback((postId, visibility) => {
    setPosts((current) => current.map((post) => post.id === postId ? { ...post, visibility } : post));
  }, []);

  const bg = isDarkMode ? "bg-slate-950 text-slate-200" : "bg-slate-50 text-slate-900";
  const cardBg = isDarkMode ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white";
  const inputClass = `w-full border px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500 focus:ring-sky-500/20" : "border-slate-300 bg-white focus:ring-sky-100"}`;

  return (
    <div className={`min-h-screen px-3 py-4 sm:px-5 lg:px-7 ${bg}`}>
      <div className="mx-auto max-w-[1440px]">
        <div className="grid items-start gap-6 xl:grid-cols-[240px_minmax(0,680px)_280px] xl:justify-center">
          <aside className="sticky top-5 hidden space-y-4 xl:block">
            <div className={`rounded-2xl border p-5 shadow-sm ${cardBg}`}><div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 font-black text-white">CN</div><h2 className="mt-4 text-lg font-black">School community</h2><p className="mt-1 text-sm leading-5 text-slate-500">Celebrate learning, people, and everyday school life.</p></div>
            <nav className={`rounded-2xl border p-3 shadow-sm ${cardBg}`} aria-label="Moment categories">{CATEGORIES.map((item) => <div key={item} className={`flex items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-50"}`}><span className="flex items-center gap-3">{categoryIcon(item)}{item}</span><span className="text-xs text-slate-400">{sortedPosts.filter((post) => post.category === item).length}</span></div>)}</nav>
          </aside>

          <main className="min-w-0">
            <header className={`mb-4 flex items-center justify-between rounded-2xl border px-5 py-4 shadow-sm ${cardBg}`}><div><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-500">{isModerator ? "Tenant communities" : "School community"}</p><h1 className="mt-0.5 text-xl font-black">Moments</h1></div><span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">Live feed</span></header>

            {canPost && <section className={`mb-4 rounded-2xl border p-4 shadow-sm ${cardBg}`}><div className="flex gap-3"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-black text-white">You</div><div className="min-w-0 flex-1 space-y-3"><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={`${inputClass} rounded-xl border-0 px-4 py-3 font-semibold ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`} placeholder="Share a school moment..." />{title && <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputClass} rounded-xl`} placeholder="Add more details" />}<div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3"><select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputClass} w-auto rounded-full py-1.5 text-xs font-bold`}>{CATEGORIES.map((item) => <option key={item}>{item}</option>)}</select><button type="button" onClick={handleSubmit} disabled={!title.trim() || submitting} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">{submitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}{submitting ? "Posting" : "Post"}</button></div></div></div></section>}

            {loadingPosts ? <div className={`flex justify-center rounded-2xl border py-16 ${cardBg}`}><FaSpinner className="animate-spin text-3xl text-blue-500" /></div> : sortedPosts.length === 0 ? <div className={`rounded-2xl border py-16 text-center text-sm ${cardBg}`}>No moments have been posted yet.</div> : <div className="space-y-4">{(isModerator ? tenantGroups : [{ id: "all", name: "", posts: sortedPosts }]).map((group) => <section key={group.id} className="space-y-4">{isModerator && <div className={`sticky top-4 z-10 flex items-center justify-between rounded-xl border px-4 py-3 shadow-sm backdrop-blur ${cardBg}`}><h2 className="font-bold">{group.name}</h2><span className="text-xs text-slate-500">{group.posts.length} moments</span></div>}{group.posts.map((post) => <PostCard key={post.id} post={post} isDarkMode={isDarkMode} canDelete={canDeletePost} onDelete={handleDeletePost} isModerator={isModerator} onVisibilityChange={handleVisibilityChange} />)}</section>)}</div>}
          </main>

          <aside className="sticky top-5 hidden space-y-4 xl:block">
            <div className={`rounded-2xl border p-5 shadow-sm ${cardBg}`}><h2 className="font-black">Community activity</h2><div className="mt-4 grid grid-cols-3 gap-2 text-center"><div><b className="block text-xl">{sortedPosts.length}</b><span className="text-[10px] uppercase text-slate-400">Posts</span></div><div><b className="block text-xl">{sortedPosts.reduce((sum, post) => sum + post.likeCount, 0)}</b><span className="text-[10px] uppercase text-slate-400">Likes</span></div><div><b className="block text-xl">{sortedPosts.reduce((sum, post) => sum + post.commentCount, 0)}</b><span className="text-[10px] uppercase text-slate-400">Replies</span></div></div></div>
            <div className={`rounded-2xl border p-5 shadow-sm ${cardBg}`}><h2 className="font-black">A positive space</h2><p className="mt-2 text-sm leading-6 text-slate-500">Share achievements, events, milestones, and useful notices. Keep every post respectful and relevant to your school community.</p></div>
          </aside>
        </div>
      </div>
    </div>
  );
}
